import { api, ApiError } from "./api";
import { db, hasPending, type MosDB, type SyncedTable, syncedTables } from "./db";

// Sync over POST /sync (D52): push the outbox and pull server changes in
// one round trip per batch. Conflict rules live on the server.

type Row = { id: string; updated_at: string; deleted_at: string | null };

/** Adopts a server row unless a newer local change for it is still queued. */
async function adopt(database: MosDB, table: SyncedTable, row: Row): Promise<void> {
	if (await hasPending(table, row.id, database)) return;
	await database.table(table).put(row);
}

// ---------------------------------------------------------------- POST /sync

type SyncChange = { table: SyncedTable; op: "upsert" | "delete"; id: string; payload: unknown };
type SyncResult = {
	table: SyncedTable;
	id: string;
	status: "ok" | "rejected" | "not_found";
	row?: Row;
	error?: { code: string; message: string };
};
type SyncResponse = {
	results: SyncResult[];
	changes: { table: SyncedTable; row: Row }[];
	cursor: string;
	has_more: boolean;
};

export type SyncResultSummary = {
	sent: number;
	rejected: number;
	pulled: number;
	stopped: "done" | "offline" | "auth" | "server";
};

const SYNC_CURSOR = "cursor:sync";
const BATCH = 100;

/**
 * One sync on POST /sync (D52): sends up to BATCH queued changes per round and
 * applies the server's results and newer rows in a single local transaction,
 * repeating until the outbox is empty and the server has nothing more.
 * Network errors and 5xx keep everything for the next run; a rejected change
 * is dropped (the server's row comes back in a later pull).
 */
export async function syncBatch(database: MosDB = db): Promise<SyncResultSummary> {
	const summary: SyncResultSummary = { sent: 0, rejected: 0, pulled: 0, stopped: "done" };
	const tables = (Object.keys(syncedTables) as SyncedTable[]).map((t) => database.table(t));
	for (let round = 0; round < 1000; round++) {
		const entries = await database.outbox.orderBy("id").limit(BATCH).toArray();
		const cursor = ((await database.meta.get(SYNC_CURSOR))?.value as string | undefined) ?? "0";
		let res: SyncResponse;
		try {
			res = await api<SyncResponse>("/sync", {
				method: "POST",
				body: {
					cursor,
					changes: entries.map<SyncChange>((e) => ({
						table: e.table,
						op: e.op,
						id: e.entityId,
						payload: e.payload,
					})),
				},
			});
		} catch (err) {
			if (!(err instanceof ApiError)) throw err;
			if (err.status === 0) return { ...summary, stopped: "offline" };
			if (err.status === 401 || err.status === 403) return { ...summary, stopped: "auth" };
			if (entries.length)
				await database.outbox.update(entries[0].id, { attempts: entries[0].attempts + 1 });
			return { ...summary, stopped: "server" };
		}

		await database.transaction("rw", [...tables, database.outbox, database.meta], async () => {
			// Results come back in the order the changes were sent.
			for (const [i, result] of res.results.entries()) {
				const entry = entries[i];
				if (!entry) continue;
				await database.outbox.delete(entry.id);
				if (result.status === "ok" && result.row) {
					summary.sent++;
					await adopt(database, result.table, result.row);
				} else if (result.status === "not_found") {
					summary.sent++; // a delete for a row the server never had
				} else {
					summary.rejected++;
					console.warn(
						`sync: rejected ${entry.op} on ${entry.table} (${result.error?.code ?? "?"})`,
					);
				}
			}
			for (const change of res.changes) await adopt(database, change.table, change.row);
			summary.pulled += res.changes.length;
			await database.meta.put({ key: SYNC_CURSOR, value: res.cursor });
		});

		const more = (await database.outbox.count()) > 0;
		if (!res.has_more && !more) return summary;
	}
	return summary;
}

let running: Promise<void> | null = null;

/**
 * Runs one sync. Concurrent calls share one run. Errors are swallowed: the
 * outbox keeps everything, and the next run retries.
 */
export function syncNow(database: MosDB = db): Promise<void> {
	running ??= (async () => {
		try {
			await syncBatch(database);
		} catch (err) {
			if (!(err instanceof ApiError)) console.warn("sync failed", err);
		} finally {
			running = null;
		}
	})();
	return running;
}
